import type { ReactNode } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { z } from 'zod';
import { AlertTriangle, Copy, ExternalLink, Loader2, Radio } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from '@/components/ui/form';
import { toast } from '@/components/ui/sonner';
import {
	INTERCOM_CREDENTIAL_MAX_LENGTH,
	INTERCOM_DEVELOPER_HUB_URL,
	INTERCOM_OWN_APP_PERMISSIONS,
	INTERCOM_OWN_APP_WEBHOOK_TOPICS
} from '../intercom-own-app-setup';
import type { UseIntercomOwnAppConnectResult } from '../hooks/use-intercom-own-app-connect';

const credential = (label: string) =>
	z
		.string()
		.trim()
		.min(1, `${label} is required`)
		.max(INTERCOM_CREDENTIAL_MAX_LENGTH, `${label} is too long`);

const ownAppFormSchema = z.object({
	accessToken: credential('Access token'),
	clientSecret: credential('Client secret')
});

type OwnAppFormData = z.infer<typeof ownAppFormSchema>;

interface SetupStepProps {
	number: number;
	title: string;
	children: ReactNode;
}

const SetupStep = ({ number, title, children }: SetupStepProps) => (
	<li className='flex gap-3'>
		<span
			aria-hidden='true'
			className='flex size-6 shrink-0 items-center justify-center rounded-full bg-muted text-xs font-semibold'
		>
			{number}
		</span>
		<div className='flex min-w-0 flex-1 flex-col gap-2'>
			<h4 className='text-sm font-medium'>{title}</h4>
			{children}
		</div>
	</li>
);

const StepText = ({ children }: { children: ReactNode }) => (
	<p className='text-xs text-muted-foreground leading-snug'>{children}</p>
);

/** A menu path inside Intercom, set apart so it reads as something to click. */
const MenuPath = ({ children }: { children: ReactNode }) => <span className='font-medium text-foreground'>{children}</span>;

const ChecklistItems = ({ label, items, monospace = false }: { label: string; items: readonly string[]; monospace?: boolean }) => (
	<ul aria-label={label} className='flex flex-col gap-1 rounded-lg bg-muted p-3'>
		{items.map((item) => (
			<li key={item} className={monospace ? 'font-mono text-[11px]' : 'text-xs'}>
				{item}
			</li>
		))}
	</ul>
);

/** What a copyable value is called: "Webhook URL copied", "Copy webhook URL". */
interface CopyableLabel {
	subject: string;
	lowercaseSubject: string;
}

const copyToClipboard = (value: string, label: CopyableLabel) =>
	navigator.clipboard
		.writeText(value)
		.then(() => toast.success(`${label.subject} copied`))
		.catch((err: unknown) => {
			console.error(`[IntercomOwnAppForm] copy ${label.lowercaseSubject} failed`, { err });
			toast.error(`Could not copy the ${label.lowercaseSubject}`);
		});

/** A value the operator pastes into Intercom, with a copy button. Null while it is still loading. */
const CopyableValue = ({ value, label }: { value: string | null; label: CopyableLabel }) => (
	<div className='flex items-center gap-1.5'>
		<code className='min-w-0 flex-1 overflow-auto whitespace-nowrap rounded-md border p-2 font-mono text-xs'>
			{value ?? 'Loading…'}
		</code>
		<Button
			type='button'
			size='icon-sm'
			variant='ghost'
			aria-label={`Copy ${label.lowercaseSubject}`}
			disabled={!value}
			onClick={() => value && void copyToClipboard(value, label)}
		>
			<Copy className='size-4' />
		</Button>
	</div>
);

const WEBHOOK_URL_LABEL: CopyableLabel = { subject: 'Webhook URL', lowercaseSubject: 'webhook URL' };

const WebhookUrlField = ({ webhookUrl, failed }: { webhookUrl: string | null; failed: boolean }) => {
	if (failed) {
		return <StepText>We could not load the webhook URL. Reopen this panel to try again.</StepText>;
	}
	return <CopyableValue value={webhookUrl} label={WEBHOOK_URL_LABEL} />;
};

type IntercomOwnAppFormProps = Pick<
	UseIntercomOwnAppConnectResult,
	'webhookUrl' | 'webhookUrlFailed' | 'isConnecting' | 'errorMessage' | 'connect'
>;

/**
 * Connect Intercom through an app the customer creates in their own workspace: three app setup
 * steps in Intercom, then the app's access token and client secret pasted here. Kim's teammate is
 * picked afterwards, on the connected card.
 */
export const IntercomOwnAppForm = ({ webhookUrl, webhookUrlFailed, isConnecting, errorMessage, connect }: IntercomOwnAppFormProps) => {
	const form = useForm<OwnAppFormData>({
		resolver: zodResolver(ownAppFormSchema),
		defaultValues: { accessToken: '', clientSecret: '' }
	});

	return (
		<Form {...form}>
			<form className='flex flex-col gap-4' onSubmit={form.handleSubmit((credentials) => connect(credentials))}>
				<ol className='flex flex-col gap-5'>
					<SetupStep number={1} title='Create an app in Intercom'>
						<StepText>
							Open the Developer Hub, click <MenuPath>New app</MenuPath>, name it <MenuPath>Kim AI</MenuPath>, pick the
							workspace you want to connect and choose <MenuPath>Internal integration</MenuPath>.
						</StepText>
						<a
							href={INTERCOM_DEVELOPER_HUB_URL}
							target='_blank'
							rel='noopener noreferrer'
							className='inline-flex items-center gap-1 text-xs font-medium underline'
						>
							Open Intercom Developer Hub
							<ExternalLink className='size-3' />
						</a>
					</SetupStep>

					<SetupStep number={2} title='Give the app these permissions'>
						<StepText>
							In your app, go to <MenuPath>Configure → Authentication</MenuPath>, click <MenuPath>Edit</MenuPath>, tick
							exactly these permissions and save.
						</StepText>
						<ChecklistItems label='Intercom permissions' items={INTERCOM_OWN_APP_PERMISSIONS} />
					</SetupStep>

					<SetupStep number={3} title='Add the webhook'>
						<StepText>
							Go to <MenuPath>Configure → Webhooks</MenuPath>, paste this URL as the endpoint, select these topics and
							save.
						</StepText>
						<WebhookUrlField webhookUrl={webhookUrl} failed={webhookUrlFailed} />
						<ChecklistItems label='Intercom webhook topics' items={INTERCOM_OWN_APP_WEBHOOK_TOPICS} monospace />
					</SetupStep>

					<SetupStep number={4} title='Paste the app credentials'>
						<FormField
							control={form.control}
							name='accessToken'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Access token</FormLabel>
									<FormControl>
										<Input type='password' autoComplete='new-password' placeholder='Paste the access token' {...field} />
									</FormControl>
									<StepText>
										Found under <MenuPath>Configure → Authentication</MenuPath>.
									</StepText>
									<FormMessage />
								</FormItem>
							)}
						/>
						<FormField
							control={form.control}
							name='clientSecret'
							render={({ field }) => (
								<FormItem>
									<FormLabel>Client secret</FormLabel>
									<FormControl>
										<Input type='password' autoComplete='new-password' placeholder='Paste the client secret' {...field} />
									</FormControl>
									<StepText>
										Found under <MenuPath>Configure → Basic information</MenuPath>. Kim uses it to verify that webhooks
										really come from your app.
									</StepText>
									<FormMessage />
								</FormItem>
							)}
						/>
					</SetupStep>
				</ol>

				{errorMessage && (
					<div role='alert' className='flex items-start gap-2 rounded-lg border p-3 text-destructive'>
						<AlertTriangle className='mt-0.5 size-4 shrink-0' />
						<span className='text-sm'>{errorMessage}</span>
					</div>
				)}

				<Button type='submit' className='w-full' size='lg' disabled={isConnecting}>
					{isConnecting ? <Loader2 className='size-4 animate-spin' /> : <Radio className='size-4' />}
					{isConnecting ? 'Connecting…' : 'Connect Intercom app'}
				</Button>
			</form>
		</Form>
	);
};
